import { useCallback, useMemo } from "react";
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
  type QueryKey,
} from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { CLOSED_STATUSES } from "@/lib/tenantJourney";
import { useTenantJourneys } from "@/hooks/v2/useTenantJourneys";
import {
  useAllActiveOccupants,
  useAllRooms,
} from "@/hooks/v2/useReassignBookings";
import { fetchAllByColumn } from "@/hooks/v2/usePropertyRentPaidStats";
import type { OwnerListRow } from "@/hooks/v2/useOwnerDetail";
import { useOwnersDirectory } from "@/hooks/v2/useOwnersDirectory";
import { useTestAccountFilters } from "@/hooks/v2/useTestAccountFilters";
import {
  RECONCILIATION_KEYS,
  useReconciliationReviewQueue,
} from "@/hooks/v2/useReconciliation";
import { BRAIN_HIDES_TEST_DATA, isBrainTestRow } from "@/lib/brain/test-data";
import {
  deriveTenantItems,
  isApplication,
  type BrainBookingRow,
  type TenantDeriveInput,
} from "@/lib/brain/derive-tenants";
import {
  derivePaymentItems,
  trackedRentBookings,
  type PaymentDeriveInput,
} from "@/lib/brain/derive-payments";
import {
  deriveOwnerItems,
  type OwnerDeriveInput,
} from "@/lib/brain/derive-owners";
import {
  deriveSupportItems,
  lastAdminReplies,
  type SupportDeriveInput,
} from "@/lib/brain/derive-support";
import { OPEN_STATUSES } from "@/lib/support-views";
import {
  computeRepresentorGaps,
  type OwnerLink,
} from "@/hooks/v2/usePropertyRepresentorGaps";
import type { ApartmentListItem, Lessor, Property } from "@/types/property";
import { isTestOwner } from "@/lib/owner-directory";
import {
  fetchRentLedgerForBookings,
  type RentLedgerWithProofs,
} from "@/lib/rent-ledger-fetch";
import type { BrainItem } from "@/lib/brain/types";
import { BRAIN_POLL } from "@/lib/brain/thresholds";
import { useBerlinDay } from "./useBerlinDay";

const NO_ITEMS: BrainItem[] = [];
const NO_BOOKINGS: BrainBookingRow[] = [];
const NO_ROOMS: TenantDeriveInput["rooms"] = [];
const NO_OCCUPANTS: TenantDeriveInput["occupants"] = new Map();
const NO_OWNERS: PaymentDeriveInput["owners"] = [];
const NO_MATCHES: PaymentDeriveInput["matches"] = [];
const NO_LEDGER: RentLedgerWithProofs = { ledger: [], proofFor: () => null };
const NO_TICKETS: SupportDeriveInput["tickets"] = [];
const NO_SUBMISSIONS: OwnerDeriveInput["submissions"] = [];
const NO_PROPERTIES: Property[] = [];
/** Ticket ids per get_issue_unread_counts call (a POST body, kept bounded). */
const UNREAD_CHUNK = 200;
/** PostgREST's per-response row cap on Supabase. */
const PAGE = 1000;

export { BRAIN_POLL };

/**
 * For queries keyed by a list of ids (journey evidence, documents, profiles,
 * ledger, ticket reads): a new id re-keys them, and without the previous rows
 * they would read as loading and unmount the open card mid-edit.
 */
// ponytail: until the new key loads, a just-added booking derives from the
// old rows (no profile/evidence yet); it corrects itself on that fetch.
const BRAIN_POLL_BY_IDS = {
  ...BRAIN_POLL,
  placeholderData: keepPreviousData,
};

/**
 * Every query key the deck derives from. `useBrainRefresh` invalidates them
 * all, so returning from a deep link never shows a card that was just
 * resolved. Each category adds the keys of the hooks it reuses.
 */
export const BRAIN_SOURCE_KEYS: QueryKey[] = [
  ["brain"],
  ["v2", "journey"],
  ["v2", "reassign", "all-rooms"],
  ["v2", "reassign", "all-occupants"],
  ["v2", "owners-list"],
  [...RECONCILIATION_KEYS.reviewQueue()],
  // The nav's reconciliation pip should agree with the cards on return.
  [...RECONCILIATION_KEYS.reviewCount()],
  // …and the Properties pip with the submission cards.
  ["v2", "pending-submission-count"],
  // The test-owner inputs: hiding a test owner in the Directory drops its cards.
  ["admin-role-mgmt-users"],
  ["test-account-filters"],
  // The Support pip should agree with the unread cards on return.
  ["admin-issues", "support-unread-total"],
];

export function useBrainRefresh(): () => Promise<void> {
  const qc = useQueryClient();
  return useCallback(async () => {
    await Promise.all(
      BRAIN_SOURCE_KEYS.map((queryKey) => qc.invalidateQueries({ queryKey }))
    );
  }, [qc]);
}

/** Every row of a totally ordered query, paged past PostgREST's row cap. */
async function fetchAllPages<Row>(
  page: (
    from: number,
    to: number
  ) => PromiseLike<{ data: unknown; error: unknown }>
): Promise<Row[]> {
  const out: Row[] = [];
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await page(from, from + PAGE - 1);
    if (error) throw error;
    const rows = (data ?? []) as Row[];
    out.push(...rows);
    if (rows.length < PAGE) return out;
  }
}

// The journey resolver reads the contract artefacts, the evidence timestamps
// and the deposit inputs (incl. the deposit discount). Drop one and a tenant
// parks at an earlier stage than their profile shows. Late rent reads the
// tenancy window (isOccupyingNow), the paid-period signals
// (isPaidCurrentPeriod) and the unit's reminder switch.
const BOOKING_COLUMNS = [
  "id, user_id, name, email, phone, status, created_at, updated_at",
  "move_in_date, move_out_date, move_out_notice_at, contract_signed_at, key_picked_up_at, key_returned_at",
  "start_date, end_date, actual_move_in_date, actual_move_out_date",
  "monthly_rent, deposit_amount, deposit_discount_mode, deposit_discount_value, rent_due_date, payment_status",
  "last_payment_received_at, rent_paid_through, manually_marked_paid, accumulated_fees_eur",
  "contract_status, contract_pdf_url, contract_url, signed_contract_url, docusign_envelope_id, docusign_status, move_in_confirmed_at",
  "move_in_reminder_24h_sent_at, move_in_reminder_36h_sent_at, move_out_reminder_12h_sent_at, move_out_reminder_36h_sent_at, move_out_reminder_48h_sent_at",
  "move_in_protocol_reopened_at, move_in_protocol_manually_closed_at, move_out_protocol_reopened_at, move_out_protocol_manually_closed_at",
  "rooms(id, title, price, deposit_amount, property_id, apartment_id, apartments(rent_reminders_enabled), properties(id, title, address, city, rent_reminders_enabled))",
].join(", ");

// Closed tenancies plus the legacy `archived` status. Only Brain skips the
// latter: adding it to CLOSED_STATUSES would change the profile's stage badge.
const NOT_OPEN = `(${[...CLOSED_STATUSES, "archived"].join(",")})`;

/** Every open (not closed, not archived) booking, paged past the row cap. */
export function useBrainBookings(enabled: boolean) {
  return useQuery({
    queryKey: ["brain", "bookings"],
    enabled,
    ...BRAIN_POLL,
    queryFn: () =>
      fetchAllPages<BrainBookingRow>((from, to) =>
        supabase
          .from("bookings")
          .select(BOOKING_COLUMNS)
          .not("status", "in", NOT_OPEN)
          .is("admin_archived_at", null)
          .order("id", { ascending: true })
          .range(from, to)
      ),
  });
}

/**
 * Drops test tenants and demo buildings on prod (isBrainTestRow). `ready` is
 * false until the admins' extra test emails are in, so no test card flashes.
 */
function useWithoutTestRows<T>(
  enabled: boolean,
  rows: T[],
  emailOf: (r: T) => string | null | undefined,
  propertyOf: (r: T) => string | null | undefined
): { rows: T[]; ready: boolean } {
  const on = enabled && BRAIN_HIDES_TEST_DATA;
  const filters = useTestAccountFilters({ enabled: on });
  const kept = useMemo(() => {
    if (!on) return rows;
    const emails = (filters.data ?? []).map((f) => f.email);
    return rows.filter(
      (r) => !isBrainTestRow(emailOf(r), propertyOf(r), emails)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [on, rows, filters.data]);
  return { rows: kept, ready: !on || !filters.isLoading };
}

export function useBrainTenantItems(enabled: boolean): {
  items: BrainItem[];
  isLoading: boolean;
  /** A source failed: the tenant cards shown (if any) are incomplete. */
  isError: boolean;
  bookings: BrainBookingRow[];
} {
  const day = useBerlinDay();
  const bookings = useBrainBookings(enabled);
  const { rows, ready: testReady } = useWithoutTestRows(
    enabled,
    (enabled && bookings.data) || NO_BOOKINGS,
    (b) => b.email,
    (b) => b.rooms?.properties?.title
  );
  // An empty list disables every journey query.
  const {
    journeys,
    isLoading: jLoading,
    isError: jError,
    isLoadingError: jMissing,
  } = useTenantJourneys(rows, BRAIN_POLL_BY_IDS);
  // Only applications show documents. The applied stage reads booking columns
  // alone, so it is known before the evidence loads.
  const appliedIds = useMemo(
    () =>
      rows
        .filter((b) => isApplication(b, journeys.get(b.id)))
        .map((b) => b.id)
        .sort((a, b) => a - b),
    [rows, journeys]
  );
  const userIds = useMemo(
    () =>
      [
        ...new Set(rows.map((b) => b.user_id).filter((u): u is string => !!u)),
      ].sort(),
    [rows]
  );

  const docs = useQuery({
    queryKey: ["brain", "tenant-documents", appliedIds.join(",")],
    enabled: appliedIds.length > 0,
    ...BRAIN_POLL_BY_IDS,
    queryFn: () =>
      fetchAllByColumn<TenantDeriveInput["documents"][number]>(
        "tenant_documents",
        "booking_id, document_type, file_url, status",
        "booking_id",
        appliedIds,
        "id",
        true
      ),
  });
  const profiles = useQuery({
    queryKey: ["brain", "profiles", userIds.join(",")],
    enabled: userIds.length > 0,
    ...BRAIN_POLL_BY_IDS,
    queryFn: async (): Promise<TenantDeriveInput["profiles"]> => {
      const list = await fetchAllByColumn<{
        id: string;
        name: string | null;
        email: string | null;
        phone: string | null;
        date_of_birth: string | null;
        gender: string | null;
      }>(
        "profiles",
        "id, name, email, phone, date_of_birth, gender",
        "id",
        userIds,
        "id",
        true,
        undefined,
        100 // UUIDs
      );
      return new Map(list.map((p) => [p.id, p]));
    },
  });
  const rooms = useAllRooms(enabled, BRAIN_POLL);
  const occupants = useAllActiveOccupants(enabled, BRAIN_POLL);
  // Checkout protocol (Neel Slack DM 2026-10-19): pending move-out date
  // requests, keyed by booking. Gates only the new move_out_requested card —
  // same "rooms gate only the roll-up" philosophy as rooms/occupants below.
  const moveOutRequests = useQuery({
    queryKey: ["brain", "move-out-requests"],
    enabled,
    ...BRAIN_POLL,
    queryFn: () =>
      fetchAllPages<{
        id: string;
        booking_id: number;
        requested_date: string;
        created_at: string;
      }>((from, to) =>
        supabase
          .from("move_out_date_requests")
          .select("id, booking_id, requested_date, created_at")
          .eq("status", "pending")
          .order("id", { ascending: true })
          .range(from, to)
      ),
  });
  const moveOutRequestsReady = !!moveOutRequests.data;
  const moveOutRequestsMap = useMemo(() => {
    const map: TenantDeriveInput["moveOutRequests"] = new Map();
    if (moveOutRequestsReady) {
      for (const r of moveOutRequests.data!) {
        map.set(r.booking_id, {
          id: r.id,
          requested_date: r.requested_date,
          created_at: r.created_at,
        });
      }
    }
    return map;
  }, [moveOutRequestsReady, moveOutRequests.data]);

  // A failed source must never read as "nothing open". Booking-level sources
  // gate every tenant card (a missing profile would read as "details missing",
  // missing evidence as an earlier stage); rooms gate only the roll-up. Cards
  // hide only when a source has NO data: a failed background refetch keeps
  // the last rows on screen, and `isError` still raises the banner.
  const coreFailed =
    bookings.isError || jError || docs.isError || profiles.isError;
  const roomsFailed = rooms.isError || occupants.isError;
  const ready =
    testReady &&
    !jMissing &&
    !!bookings.data &&
    !jLoading &&
    (appliedIds.length === 0 || !!docs.data) &&
    (userIds.length === 0 || !!profiles.data);
  const roomsReady = !!rooms.data && !!occupants.data;

  const items = useMemo(
    () =>
      enabled && ready
        ? deriveTenantItems(
            {
              bookings: rows,
              journeys,
              documents: docs.data ?? [],
              profiles: profiles.data ?? new Map(),
              rooms: roomsReady ? rooms.data! : NO_ROOMS,
              occupants: roomsReady ? occupants.data! : NO_OCCUPANTS,
              moveOutRequests: moveOutRequestsMap,
            },
            new Date()
          )
        : NO_ITEMS,
    // `day` re-derives date-threshold cards after Berlin midnight.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      enabled,
      ready,
      roomsReady,
      rows,
      journeys,
      docs.data,
      profiles.data,
      rooms.data,
      occupants.data,
      moveOutRequestsMap,
      day,
    ]
  );
  return {
    items,
    bookings: rows,
    isError: enabled && (coreFailed || roomsFailed),
    isLoading:
      bookings.isLoading ||
      jLoading ||
      docs.isLoading ||
      profiles.isLoading ||
      rooms.isLoading ||
      occupants.isLoading,
  };
}

/**
 * The owner rows the Owners page counts with its default toggle: the merged
 * directory (billing + account halves) minus test owners (`isTestOwner`, both
 * halves plus the demo flag). Payout cards therefore equal the "Payouts
 * ready" tile, platform lessor included. `rows` stays undefined until every
 * input that decides "test owner" is in, as the page waits for them. Shares
 * the page's cache entries; Task 7 reads the same rows.
 */
export function useBrainOwnersRows(enabled: boolean): {
  rows: OwnerListRow[] | undefined;
  isLoading: boolean;
  isError: boolean;
  /** A payouts read failed: every group is unknown, not "nothing owed". */
  degraded: boolean;
} {
  const dir = useOwnersDirectory(undefined, { enabled, poll: BRAIN_POLL });
  const filters = useTestAccountFilters({ enabled });
  const isLoading = dir.isLoading || dir.accountsLoading || filters.isLoading;
  const rows = useMemo(() => {
    if (!enabled || isLoading || !dir.lessors) return undefined;
    const emails = (filters.data ?? []).map((f) => f.email);
    return dir.allRows
      .filter((r) => r.lessor && !isTestOwner(r, emails))
      .map((r) => r.lessor!);
  }, [enabled, isLoading, dir.lessors, dir.allRows, filters.data]);
  return {
    rows,
    isLoading,
    isError: enabled && dir.error != null,
    degraded: !!dir.lessors?.some((o) => o.payoutDataDegraded),
  };
}

/**
 * Payout, reconciliation and late-rent cards. Late rent derives from the
 * tenants category's bookings, so it follows that category's readiness (and
 * its failure flag), plus the rent ledger of the tenancies it tracks.
 */
export function useBrainPaymentItems(
  enabled: boolean,
  bookings: BrainBookingRow[]
): { items: BrainItem[]; isLoading: boolean; isError: boolean } {
  const day = useBerlinDay();
  const owners = useBrainOwnersRows(enabled);
  const queue = useReconciliationReviewQueue(enabled, BRAIN_POLL);
  // Open past months, for the same tenancies the Reminders tab tracks.
  const trackedIds = useMemo(
    () =>
      trackedRentBookings(bookings, new Date())
        .map((b) => b.id)
        .sort((a, b) => a - b),
    // `day`: occupancy windows open and close at midnight.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bookings, day]
  );
  const ledgerQuery = useQuery({
    queryKey: ["brain", "rent-ledger", trackedIds.join(",")],
    enabled: enabled && trackedIds.length > 0,
    ...BRAIN_POLL_BY_IDS,
    queryFn: () => fetchRentLedgerForBookings(supabase, trackedIds),
  });
  const ledger = trackedIds.length ? (ledgerQuery.data ?? null) : NO_LEDGER;
  // A failed source withholds only its own cards, and only while it has no
  // data: a failed background refetch keeps the last rows (see tenants).
  const items = useMemo(
    () =>
      enabled
        ? derivePaymentItems(
            {
              owners: owners.rows ?? NO_OWNERS,
              matches: queue.data ?? NO_MATCHES,
              bookings,
              ledger,
            },
            new Date()
          )
        : NO_ITEMS,
    // `day` re-derives late rent after Berlin midnight.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enabled, owners.rows, queue.data, bookings, ledger, day]
  );
  return {
    items,
    isLoading: owners.isLoading || queue.isLoading || ledgerQuery.isLoading,
    // A degraded payouts read reads as "—" on the Owners page; here too it
    // must never read as "nothing owed".
    isError:
      enabled &&
      (owners.isError ||
        owners.degraded ||
        queue.isError ||
        ledgerQuery.isError),
  };
}

/** One whole-table Brain read, paged, polling with the deck. */
function useBrainRows<Row>(
  name: string,
  enabled: boolean,
  page: Parameters<typeof fetchAllPages>[0]
) {
  return useQuery({
    queryKey: ["brain", name],
    enabled,
    ...BRAIN_POLL,
    queryFn: () => fetchAllPages<Row>(page),
  });
}

/**
 * A portfolio-wide read narrowed to one property, in the shape
 * computeRepresentorGaps takes. Rows kept from an earlier fetch stay usable
 * after a failed refetch; with no rows, the property is pending or failed.
 */
function forProperty<T>(q: { data?: unknown; isError: boolean }, data: T) {
  return q.data !== undefined
    ? { data, isPending: false, isError: false }
    : { data: undefined, isPending: !q.isError, isError: q.isError };
}

function groupBy<T>(rows: readonly T[], key: (r: T) => string) {
  const map = new Map<string, T[]>();
  for (const r of rows) {
    const k = key(r);
    const list = map.get(k);
    if (list) list.push(r);
    else map.set(k, [r]);
  }
  return map;
}

/**
 * Owner cards: onboarding submissions, unsigned invites and missing bank
 * details (the Owners list's rows), property submissions, contract ends and
 * representor gaps. Submissions and contract ends read the same paged
 * properties/apartments tables as the gaps, which run the property sidebar's
 * own resolver instead of its per-property queries.
 */
export function useBrainOwnerItems(enabled: boolean): {
  items: BrainItem[];
  isLoading: boolean;
  isError: boolean;
} {
  const day = useBerlinDay();
  const owners = useBrainOwnersRows(enabled);
  const submissions = useBrainRows<OwnerDeriveInput["submissions"][number]>(
    "owner-onboarding",
    enabled,
    (from, to) =>
      supabase
        .from("owner_onboarding_submissions")
        .select("id, status, name, email, submitted_at")
        .eq("status", "pending")
        .order("id")
        .range(from, to)
  );
  const properties = useBrainRows<Property>("properties", enabled, (from, to) =>
    supabase.from("properties").select("*").order("id").range(from, to)
  );
  // Test submissions (Vishnu's, Neel's) and demo buildings stay off prod.
  const { rows: realSubmissions, ready: submissionsReady } = useWithoutTestRows(
    enabled,
    submissions.data ?? NO_SUBMISSIONS,
    (s) => s.email,
    () => null
  );
  const { rows: realProperties, ready: propertiesReady } = useWithoutTestRows(
    enabled,
    properties.data ?? NO_PROPERTIES,
    () => null,
    (p) => p.title
  );
  const apartments = useBrainRows<ApartmentListItem>(
    "apartments",
    enabled,
    // apartments has no archived_at on prod (dev only); filtering on a
    // missing column fails the whole request and withholds every owner card.
    (from, to) =>
      supabase.from("apartments").select("*").order("id").range(from, to)
  );
  // create-contract's order, so each property's rows[0] is its fallback pick.
  const links = useBrainRows<OwnerLink & { property_id: number | string }>(
    "property-owner-links",
    enabled,
    (from, to) =>
      supabase
        .from("property_owners")
        .select("user_id, lessor_id, property_id")
        .order("lessor_id", { ascending: false, nullsFirst: false })
        .order("created_at", { ascending: true })
        .order("id")
        .range(from, to)
  );
  const rooms = useBrainRows<
    NonNullable<OwnerDeriveInput["setup"]>["rooms"][number]
  >("rooms-setup", enabled, (from, to) =>
    // rooms has no archived_at on prod either (see apartments above).
    supabase
      .from("rooms")
      .select(
        "id, property_id, apartment_id, lessor_override, title, price, deposit_amount, pickup_method, pickup_instructions, keynest_partner_shop_name, keynest_partner_shop_address, keynest_access_url, keynest_pickup_code"
      )
      .order("id")
      .range(from, to)
  );
  const lessors = useBrainRows<Lessor>("lessor-reps", enabled, (from, to) =>
    supabase
      .from("lessors")
      .select("id, rep_name, rep_role, rep_note")
      .order("id")
      .range(from, to)
  );

  const items = useMemo(
    () => {
      if (!enabled) return NO_ITEMS;
      // A submitted building under review (or turned down) isn't live: it gets
      // the submission card, not contract-end or representor cards.
      const live = realProperties.filter(
        (p) => p.approval_status === "approved"
      );
      const titleById = new Map(live.map((p) => [String(p.id), p.title]));
      // String keys: PostgREST's bigint ids don't always come back as numbers.
      const aptsBy = groupBy(apartments.data ?? [], (a) =>
        String(a.property_id)
      );
      const linksBy = groupBy(links.data ?? [], (l) => String(l.property_id));
      return deriveOwnerItems(
        {
          owners: owners.rows ?? [],
          submissions: realSubmissions,
          propertySubmissions: realProperties,
          leases: [
            ...live.map((p) => ({
              scope: "property" as const,
              id: Number(p.id),
              propertyId: Number(p.id),
              label: p.title,
              lease_end: p.lease_end,
            })),
            ...(apartments.data ?? [])
              .filter((a) => titleById.has(String(a.property_id)))
              .map((a) => ({
                scope: "apartment" as const,
                id: Number(a.id),
                propertyId: Number(a.property_id),
                label:
                  [titleById.get(String(a.property_id)), a.label]
                    .filter(Boolean)
                    .join(" · ") || null,
                lease_end: a.lease_end,
              })),
          ],
          representorGaps: live.map((p) => {
            const r = computeRepresentorGaps({
              property: p,
              apartments: forProperty(
                apartments,
                aptsBy.get(String(p.id)) ?? []
              ),
              ownerLinks: forProperty(links, linksBy.get(String(p.id)) ?? []),
              lessors: forProperty(lessors, lessors.data),
            });
            return {
              propertyId: Number(p.id),
              label: p.title,
              missingCount: r.missing.length,
              pending: r.pending,
              failed: r.failed,
            };
          }),
          // No card from a partial read: a missing table reads as all gaps.
          setup:
            apartments.data && rooms.data
              ? {
                  properties: live,
                  apartments: apartments.data,
                  rooms: rooms.data,
                }
              : undefined,
        },
        new Date()
      );
    },
    // `day` re-derives contract-end cards after Berlin midnight.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      enabled,
      owners.rows,
      realSubmissions,
      realProperties,
      apartments.data,
      apartments.isError,
      links.data,
      links.isError,
      lessors.data,
      lessors.isError,
      rooms.data,
      day,
    ]
  );
  const sources = [submissions, properties, apartments, links, lessors, rooms];
  return {
    items,
    isLoading:
      owners.isLoading ||
      sources.some((q) => q.isLoading) ||
      !submissionsReady ||
      !propertiesReady,
    isError: enabled && (owners.isError || sources.some((q) => q.isError)),
  };
}

/**
 * Ticket cards: new and unassigned, unread messages (the caller's own, via
 * get_issue_unread_counts, as the Support pip counts them), and no real
 * tenant-visible admin reply for STALE_TICKET_DAYS — internal notes and the
 * database-marked system events don't count. Each
 * card carries the ticket's own assignee. Unread and reply reads withhold
 * only their own cards while they have no data.
 */
export function useBrainSupportItems(enabled: boolean): {
  items: BrainItem[];
  isLoading: boolean;
  isError: boolean;
} {
  const day = useBerlinDay();
  const { user } = useAuth();
  const me = user?.id ?? "";
  const tickets = useBrainRows<SupportDeriveInput["tickets"][number]>(
    "tickets",
    enabled,
    (from, to) =>
      supabase
        .from("issues")
        .select(
          "id, title, status, assigned_to, archived_at, snoozed_until, created_at, last_message_at, bookings(email), properties(title)"
        )
        .is("archived_at", null)
        .in("status", OPEN_STATUSES)
        .order("id")
        .range(from, to)
  );
  const { rows: list, ready: testReady } = useWithoutTestRows(
    enabled,
    (enabled && tickets.data) || NO_TICKETS,
    (t) => t.bookings?.email,
    (t) => t.properties?.title
  );
  const ids = useMemo(() => list.map((t) => t.id), [list]);
  const replies = useQuery({
    queryKey: ["brain", "ticket-admin-replies", ids.join(",")],
    enabled: ids.length > 0,
    ...BRAIN_POLL_BY_IDS,
    queryFn: async () =>
      lastAdminReplies(
        await fetchAllByColumn<{
          issue_id: string;
          created_at: string;
          comment: string | null;
        }>(
          "issue_comments",
          "issue_id, created_at, comment",
          "issue_id",
          ids,
          "id",
          true,
          // A reply the tenant can read: not soft-deleted, not an internal note.
          (q) =>
            q
              .eq("sender_scope", "admin")
              .eq("is_system", false)
              .is("deleted_at", null)
              .or("is_internal.is.null,is_internal.eq.false"),
          100 // UUIDs
        )
      ),
  });
  const unread = useQuery({
    // Per caller: the RPC counts auth.uid()'s unread (the pip keys on it too).
    queryKey: ["brain", "ticket-unread", me, ids.join(",")],
    enabled: !!me && ids.length > 0,
    ...BRAIN_POLL_BY_IDS,
    queryFn: async () => {
      const counts = new Map<string, number>();
      for (let i = 0; i < ids.length; i += UNREAD_CHUNK) {
        const { data, error } = await supabase.rpc("get_issue_unread_counts", {
          issue_ids: ids.slice(i, i + UNREAD_CHUNK),
          include_internal: true,
        });
        if (error) throw error;
        for (const r of (data ?? []) as {
          issue_id: string;
          unread_count: unknown;
        }[])
          counts.set(r.issue_id, Number(r.unread_count) || 0);
      }
      return counts;
    },
  });

  const items = useMemo(
    () =>
      enabled && testReady
        ? deriveSupportItems(
            {
              tickets: list,
              lastAdminReplyAt: replies.data ?? null,
              unread: unread.data ?? null,
            },
            new Date()
          )
        : NO_ITEMS,
    // `day` re-derives stale cards after Berlin midnight.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enabled, testReady, list, replies.data, unread.data, day]
  );
  return {
    items,
    isLoading: tickets.isLoading || replies.isLoading || unread.isLoading,
    isError: enabled && (tickets.isError || replies.isError || unread.isError),
  };
}
